import { env } from '@/lib/env'
import {
  getSelectedInstitutionId,
  ApiClientError,
} from '@/lib/api/client'
import type { CampaignSalesFilters } from '@/lib/campaign-reports'
import type {
  CampaignSaleDetailPayload,
  CampaignSaleDetailQuestionnaireAnswer,
  CampaignSaleListItem,
} from '@/types/campaign-report'

export type PedidosExportFormat = 'padrao' | 'scpc'

export const PEDIDOS_EXPORT_FORMAT_OPTIONS: {
  value: PedidosExportFormat
  label: string
}[] = [
  { value: 'padrao', label: 'Relatório padrão' },
  { value: 'scpc', label: 'Exportação Padrão SCPC' },
]

export const PADRAO_EXPORT_BASE_HEADER = [
  'ID',
  'Status',
  'Data pagamento',
  'Data criação',
  'Cliente',
  'Email',
  'Telefone',
  'Total',
  'Qtd',
  'Método',
  'Afiliado',
  'UTM Source',
  'UTM Campaign',
  'Número da Sorte',
] as const

export function csvEscape(value: string | number | null | undefined): string {
  const text = String(value ?? '')
  return `"${text.replace(/"/g, '""')}"`
}

type QuestionColumn = {
  questionId: number
  header: string
}

export function collectQuestionColumns(
  details: CampaignSaleDetailPayload[]
): QuestionColumn[] {
  const columns: QuestionColumn[] = []
  const seenQuestionIds = new Set<number>()

  for (const detail of details) {
    for (const answer of detail.questionnaire?.answers ?? []) {
      if (seenQuestionIds.has(answer.question_id)) continue
      seenQuestionIds.add(answer.question_id)
      columns.push({
        questionId: answer.question_id,
        header: answer.question_title,
      })
    }
  }

  const titleCounts = new Map<string, number>()
  for (const column of columns) {
    titleCounts.set(column.header, (titleCounts.get(column.header) ?? 0) + 1)
  }

  return columns.map((column) => ({
    ...column,
    header:
      (titleCounts.get(column.header) ?? 0) > 1
        ? `${column.header} (#${column.questionId})`
        : column.header,
  }))
}

function answersByQuestionId(
  answers: CampaignSaleDetailQuestionnaireAnswer[]
): Map<number, string> {
  const map = new Map<number, string>()
  for (const answer of answers) {
    map.set(answer.question_id, answer.option_label)
  }
  return map
}

export type BuildPadraoExportCsvParams = {
  sales: CampaignSaleListItem[]
  details: CampaignSaleDetailPayload[]
  formatDateTime: (iso: string) => string
  displayStatusLabel: (sale: CampaignSaleListItem) => string
  resolveTrackingSource: (sale: CampaignSaleListItem) => string
  resolveTrackingCampaign: (sale: CampaignSaleListItem) => string
}

export function buildPadraoExportCsv({
  sales,
  details,
  formatDateTime,
  displayStatusLabel,
  resolveTrackingSource,
  resolveTrackingCampaign,
}: BuildPadraoExportCsvParams): string {
  const questionColumns = collectQuestionColumns(details)
  const header = [
    ...PADRAO_EXPORT_BASE_HEADER,
    ...questionColumns.map((column) => column.header),
  ]

  const lines: string[] = []

  for (let index = 0; index < sales.length; index += 1) {
    const sale = sales[index]
    const detail = details[index]
    const answerMap = answersByQuestionId(detail.questionnaire?.answers ?? [])
    const luckyNumbers = detail.lucky_numbers.length > 0 ? detail.lucky_numbers : ['']

    for (const fullNumber of luckyNumbers) {
      const baseCells = [
        sale.payment_id,
        csvEscape(displayStatusLabel(sale)),
        csvEscape(sale.paid_at ? formatDateTime(sale.paid_at) : ''),
        csvEscape(formatDateTime(sale.created_at)),
        csvEscape(sale.donor_name ?? ''),
        csvEscape(sale.email ?? ''),
        csvEscape(sale.phone ?? ''),
        String(sale.amount),
        String(sale.quantity),
        csvEscape((sale.payment_provider ?? '').toUpperCase()),
        csvEscape(sale.affiliate?.tracking_code ?? ''),
        csvEscape(resolveTrackingSource(sale)),
        csvEscape(resolveTrackingCampaign(sale)),
        csvEscape(fullNumber),
      ]
      const questionCells = questionColumns.map((column) =>
        csvEscape(answerMap.get(column.questionId) ?? '')
      )

      lines.push([...baseCells, ...questionCells].join(','))
    }
  }

  return [header.join(','), ...lines].join('\n')
}

function buildSalesExportQueryString(
  filters: CampaignSalesFilters,
  format: PedidosExportFormat
): string {
  const search = new URLSearchParams()
  const statuses = filters.statuses?.filter(Boolean)
  if (statuses && statuses.length > 0) {
    search.set('status', statuses.join(','))
  }
  if (filters.paymentProvider) {
    search.set('payment_provider', filters.paymentProvider)
  }
  if (filters.dateFrom) search.set('date_from', filters.dateFrom)
  if (filters.dateTo) search.set('date_to', filters.dateTo)
  if (filters.dateBasis) search.set('date_basis', filters.dateBasis)
  const q = filters.q?.trim()
  if (q) search.set('q', q)
  search.set('format', format)
  return `?${search.toString()}`
}

async function downloadCampaignSalesExport(
  campaignId: number,
  filters: CampaignSalesFilters,
  format: PedidosExportFormat,
  filename: string
): Promise<void> {
  const baseUrl = env.apiBaseUrl.replace(/\/$/, '')
  const query = buildSalesExportQueryString(filters, format)
  const url = `${baseUrl}/api/campaigns/${campaignId}/sales/export${query}`

  const headers: Record<string, string> = {
    Accept: 'text/csv',
  }
  const institutionId = getSelectedInstitutionId()
  if (institutionId) {
    headers['X-Institution-Id'] = institutionId
  }

  const response = await fetch(url, { method: 'GET', headers, credentials: 'include' })

  if (!response.ok) {
    let message = `Erro HTTP ${response.status}`
    const contentType = response.headers.get('content-type')?.toLowerCase() ?? ''
    if (contentType.includes('application/json')) {
      try {
        const json = (await response.json()) as { message?: string }
        if (json.message) message = json.message
      } catch {
        // ignore
      }
    }
    throw new ApiClientError(message, response.status)
  }

  const blob = await response.blob()
  const blobUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  a.click()
  URL.revokeObjectURL(blobUrl)
}

export async function downloadCampaignSalesPadraoExport(
  campaignId: number,
  filters: CampaignSalesFilters
): Promise<void> {
  return downloadCampaignSalesExport(
    campaignId,
    filters,
    'padrao',
    `pedidos-campanha-${campaignId}.csv`
  )
}

export async function downloadCampaignSalesScpcExport(
  campaignId: number,
  filters: CampaignSalesFilters
): Promise<void> {
  return downloadCampaignSalesExport(
    campaignId,
    filters,
    'scpc',
    `carga-participantes-campanha-${campaignId}.csv`
  )
}
